import classNames from 'clsx';
import { Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';

import { ReactComponent as MemberIcon } from '@/img/icons/member-icon.svg';
import { ReactComponent as ChevronDownIcon } from '@/img/icons/chevron-down.svg';

import ProfileStore from '../../../../stores/ProfileStore';
import PointsStore from '@/stores/PointsStore';
import PointsConstants from '@/constants/PointsConstants';
import Analytics from 'arbitrip-common/client/analytics';
import Config from 'arbitrip-common/client/utils/Config';
import { getMatmidTierDisplayName } from 'arbitrip-common/client/utils/MatmidTierUtils';
import ApiURLs from '../../../../utils/ApiURLs';
import { isElalCompany } from '@/utils/isCompany';

const MemberComponent = ({ profile }) => {
    const { t } = useTranslation();
    const isElal = isElalCompany();

    const [open, setOpen] = useState(false);
    const menuRef = useRef(null);
    const buttonRef = useRef(null);

    const points = PointsStore.getPoints();

    const [pointsBalance, setPointsBalance] = useState(() => {
        return points.balance ?? 0;
    });
    const [showPoints, setShowPoints] = useState(() => {
        return points.show ?? false;
    });
    const [pointsStatus, setPointsStatus] = useState(() => {
        return PointsStore.getStatus();
    });

    const formattedPoints = pointsBalance.toLocaleString('en-US');
    const loyaltyTierDisplayName = getMatmidTierDisplayName(points?.tier);

    // Early return if NOT Elal theme
    if (!isElal) {
        return null;
    }

    // Update points and status when PointsStore changes
    useEffect(() => {
        const handlePointsChange = () => {
            const points = PointsStore.getPoints();
            setPointsBalance(points.balance ?? 0);
            setShowPoints(points.show ?? false);
            setPointsStatus(PointsStore.getStatus());
        };
        
        handlePointsChange();

        PointsStore.addChangeListener(handlePointsChange);

        return () => {
            PointsStore.removeChangeListener(handlePointsChange);
        };
    }, []);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(e.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const logoutClickHandler = () => {
        Analytics.endSession(profile);
        Analytics.actions.global_properties.clear();

        function deleteCookieBy(name) {
            document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        }

        deleteCookieBy('logged_in');
        console.log('should invoke logout event');

        ProfileStore.clear();

        document.location.reload();
    };

    // Show loader while loading (status is BUSY or INITIAL)
    const isLoading = pointsStatus === PointsConstants.STATUS.BUSY || pointsStatus === PointsConstants.STATUS.INITIAL;

    
    // If status is SUCCESS but showPoints is false, don't show component
    if (pointsStatus === PointsConstants.STATUS.SUCCESS && !showPoints) {
        return null;
    }


    return (
        <div className="member-component">
            <Button
                ref={buttonRef}
                className={classNames('member-component__button', { open })}
                aria-controls="member-menu"
                aria-haspopup="true"
                aria-expanded={open}
                data-testid="elal-member-menu-toggler"
                onClick={() => setOpen(!open)}
                disableRipple
            >
                <MemberIcon aria-hidden="true" />

                <div className="member-component__info">
                    <span  className={`member-component__name ${!isLoading ? 'loaded' : ''}`}>{profile.first_name}</span>
                    {!isLoading &&
                    <span className="member-component__points">
                        {formattedPoints} {t('PointsShortcut')}
                    </span>
                    }
                </div>

                <div className={`member-component__chevron ${open ? 'open' : ''}`} aria-hidden="true">
                    <ChevronDownIcon />
                </div>
            </Button>

            {open && (
                <div ref={menuRef} id="member-menu" className="member-component__dropdown" role="menu">
                    <div className="member-component__dropdown-header">
                        <div className="member-component__dropdown-header-info">
                            <div className="member-component__dropdown-name">{profile.full_name}</div>
                            {points?.member_number && (
                                <div className="member-component__dropdown-number">
                                    {t('MemberNumber')}: {points.member_number}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="member-component__dropdown-details">
                        {loyaltyTierDisplayName && (
                            <div className="member-component__dropdown-details-first">
                                <div className="label">{t('Tier')}</div>
                                <div className="value">
                                    {loyaltyTierDisplayName}
                                </div>
                            </div>
                        )}
                        <div className="member-component__dropdown-details-second">
                            <div className="label">{t('TotalPoints')}</div>
                            <div className="value">{formattedPoints}</div>
                        </div>
                    </div>
                        <div className="member-component__dropdown-logout">
                    <a
                        href={Config.prod ? '/logout' : ApiURLs.LOGOUT}
                        data-testid="elal-logout-link"
                        onClick={logoutClickHandler}
                    >
                        {t('Logoff')}
                    </a>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MemberComponent;
